import React from 'react';
import { DSCard, DSButton, DSInput, DSBackLink, DSBadge } from '../../design-system/primitives';
import './mergeAccounts.css';

// Choosing the two accounts to merge.
//
// This used to ask which account to DELETE, in red, next to which to KEEP, in
// green - and then the merge screen asked the same question again with "Make
// primary", where it belongs, because that is where you can see what each
// account is carrying. Asking first, from a search result showing a name and a
// balance, is asking someone to decide with nothing to decide on.
//
// So this picks two people and stops. No roles, no colour, no warning about
// deletion - which was not true anyway, since nothing is deleted.

function MergePicker({
  search,
  onSearchChange,
  results,
  selected,
  onSelect,
  onRemove,
  onContinue,
  onBack
}) {
  const full = selected.length >= 2;

  return (
    <DSCard cornerVariant="lg" className="merge-picker">
      <div className="merge-picker__head">
        <div>
          <h2 className="merge-picker__title">Merge duplicate accounts</h2>
          <p className="merge-picker__lede">
            Pick the two accounts that are the same person. You choose which one to
            keep on the next screen, where you can see what each is carrying.
          </p>
        </div>
        <DSBackLink tone="subtle" size="sm" onClick={onBack}>Back to Users</DSBackLink>
      </div>

      <DSInput
        label="Search users"
        type="search"
        placeholder="Search by name or email..."
        value={search}
        onChange={onSearchChange}
        disabled={full}
      />

      {full && (
        <p className="merge-picker__note">
          Two accounts chosen. Remove one to search for a different person.
        </p>
      )}

      {selected.length > 0 && (
        <ul className="merge-picker__chosen">
          {selected.map((user) => (
            <li key={user.id}>
              <span className="merge-picker__chosen-who">
                <strong>{fullName(user)}</strong>
                <span>{user.email}</span>
              </span>
              <DSButton
                tone="secondary"
                appearance="text"
                size="sm"
                onClick={() => onRemove(user.id)}
              >
                Remove
              </DSButton>
            </li>
          ))}
        </ul>
      )}

      {!full && results.length > 0 && (
        <ul className="merge-picker__results">
          {results.map((user) => {
            const chosen = selected.some((s) => s.id === user.id);
            return (
              <li key={user.id}>
                <span className="merge-picker__who">
                  <strong>{fullName(user)}</strong>
                  <span className="merge-picker__meta">
                    {user.email}
                    {Number(user.credit_balance) > 0 && ` · $${Number(user.credit_balance).toFixed(2)} credit`}
                  </span>
                </span>
                {user.is_member && <DSBadge variant="success">Member</DSBadge>}
                <DSButton
                  tone="secondary"
                  appearance="outline"
                  size="sm"
                  disabled={chosen}
                  onClick={() => onSelect(user)}
                >
                  {chosen ? 'Chosen' : 'Choose'}
                </DSButton>
              </li>
            );
          })}
        </ul>
      )}

      {search.length >= 2 && results.length === 0 && !full && (
        <p className="merge-picker__note">Nobody matches that.</p>
      )}

      <DSButton
        tone="primary"
        appearance="solid"
        className="merge-picker__continue"
        disabled={!full}
        onClick={onContinue}
      >
        Compare these accounts
      </DSButton>
    </DSCard>
  );
}

function fullName(user) {
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
}

export default MergePicker;
